import { useRouter } from 'vue-router';
import { useAuthStore } from '@/core/stores/auth.store';
import { useFluidStore } from '@/core/stores/fluid.store';
import { useNotificationsStore } from '@/core/stores/notifications.store';
import { useEmotionCheckinStore } from '@/core/stores/emotion-checkin.store';
import notificationsService from '@/shared/services/notifications.service';
import fluidService from '@/shared/services/fluid.service';
import { FLUID_NOTIFICATION } from '@/features/fluid/constants/notification';
import { NOTIFICATION_CODE } from '../constants/notification';

/** Inbox clicks and native pushes resolve the same saved notification and current clinical context. */
export function useNotificationPresenter() {
  const auth = useAuthStore();
  const fluid = useFluidStore();
  const inbox = useNotificationsStore();
  const checkin = useEmotionCheckinStore();
  const router = useRouter();

  async function present(
    itemUnique,
    { fromPush = false, tapped = false, canPresent = () => true } = {},
  ) {
    const session = auth.authStateVersion;
    let current = await notificationsService.get(itemUnique);
    // Lighter band notices remain unread in the bell; native receipt must not interrupt the patient.
    if (current.code === FLUID_NOTIFICATION.NOTICE && fromPush && !tapped) {
      return false;
    }
    if ((!current.actionable || current.deferred) && fromPush && !tapped) {
      return false;
    }

    // Push payloads contain identity only. Do not trust their copied clinical fields or old eligibility.
    const data = {
      notificationCode: current.code,
      itemUnique: current.itemUnique,
      refUnique: current.sourceUnique,
      scheduledAtUtc: current.snapshot.occurrence?.scheduledAtUtc,
    };
    let context;
    if (
      Object.values(FLUID_NOTIFICATION).includes(current.code) &&
      current.code !== FLUID_NOTIFICATION.NOTICE &&
      (current.actionable ||
        (current.code === FLUID_NOTIFICATION.OVERDUE &&
          !current.expired &&
          current.outcome === 'DISMISSED'))
    ) {
      context = await fluidService.getNotificationContext(data);
      if (context.dryWeightUpdate) {
        context.dryWeightUpdate.itemUnique = current.itemUnique;
      }
    }
    if (context && !context.actionable && fromPush && !tapped) {
      return false;
    }
    if (!canPresent()) {
      return false;
    }
    if (context?.actionable && tapped) {
      await router.push('/fluid');
    } else if (current.code === NOTIFICATION_CODE.MEDICATION_REMINDER && tapped) {
      await router.push('/medications');
    }
    if (session !== auth.authStateVersion) {
      return false;
    }
    if (!canPresent()) {
      return false;
    }
    // Do not open a detail that the API could not authorize and mark read.
    current = await inbox.read(current.itemUnique);
    // The completed read may hand control to a different login or patient before this caller resumes.
    if (session !== auth.authStateVersion || !canPresent()) {
      return false;
    }
    if (current.code === NOTIFICATION_CODE.EMOTION_CHECKIN && current.actionable) {
      await router.push('/home');
      if (session !== auth.authStateVersion) {
        return false;
      }
      await checkin.check();
      return session === auth.authStateVersion;
    }
    if (
      context?.actionable &&
      (current.actionable ||
        (current.code === FLUID_NOTIFICATION.OVERDUE &&
          current.outcome === 'DISMISSED' &&
          !current.expired))
    ) {
      if (context.dashboard) {
        fluid.applyDashboard(context.dashboard, session);
      }
      fluid.showPrompt(context, data);
    } else if (!fromPush || tapped || !context) {
      inbox.selected = current;
    } else {
      return false;
    }
    return session === auth.authStateVersion;
  }

  return { present };
}
